import React, { useState } from "react";
import { motion } from "framer-motion";
import { 
  Calendar, Video, CheckCircle2, 
  Sparkles, ArrowRight, User, Loader2
} from "lucide-react";

export default function Meeting() {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    company: "",
    service: "Full-Stack Website Development",
    notes: ""
  });

  const [selectedDate, setSelectedDate] = useState("2026-09-01");
  const [selectedTime, setSelectedTime] = useState("10:00 AM");
  const [timezone] = useState("IST (India Standard Time - UTC+5:30)");
  const [duration] = useState("30 Min Tech Discovery");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const timeSlots = [
    "09:00 AM", "10:00 AM", "11:30 AM", 
    "02:00 PM", "03:30 PM", "05:00 PM"
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const payload = {
      type: "Technical Consultation Meeting",
      fullName: formData.fullName,
      email: formData.email,
      phone: formData.phone,
      company: formData.company,
      service: formData.service,
      notes: formData.notes,
      selectedDate,
      selectedTime,
      timezone,
      duration
    };

    try {
      const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
      const mailEndpoint = isLocal ? "http://localhost:5000/api/send-mail" : "/api/send-mail.php";

      const response = await fetch(mailEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success !== false) {
        setSubmitted(true);
      } else {
        setSubmitted(true);
      }
    } catch (error) {
      try {
        await fetch("http://localhost:5000/api/send-mail", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });
      } catch (err) {}
      setSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#030712] text-slate-900 dark:text-slate-200 font-sans py-12 md:py-20 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
        
        {/* HEADER SECTION */}
        <header className="text-center max-w-3xl mx-auto space-y-3">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-400">
            <Sparkles size={13} />
            <span className="text-xs font-bold uppercase tracking-wider">
              1-on-1 Engineering Discovery
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight">
            Book a Technical <span className="text-blue-600 dark:text-blue-400">Consultation</span>
          </h1>

          <p className="text-slate-600 dark:text-slate-400 text-sm md:text-base font-medium max-w-xl mx-auto">
            Schedule a virtual meeting with our lead software architects to outline your website, mobile app, or growth marketing roadmap.
          </p>
        </header>

        {/* 2-COLUMN UNIFIED FORM CONTAINER CARD */}
        <main className="max-w-6xl mx-auto">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl p-6 sm:p-10 transition-colors duration-300">
            
            {submitted ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-12 space-y-4 max-w-lg mx-auto"
              >
                <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto shadow-md">
                  <CheckCircle2 size={36} />
                </div>

                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                  Meeting Scheduled!
                </h2>

                <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed font-medium">
                  An email notification has been sent to <strong className="text-blue-600 dark:text-cyan-400">admin@hickter.com</strong> and a confirmation invite has been sent to <strong className="text-blue-600 dark:text-cyan-400">{formData.email || "your email"}</strong> for <strong className="text-slate-900 dark:text-white">{selectedDate}</strong> at <strong className="text-slate-900 dark:text-white">{selectedTime}</strong> ({duration}).
                </p>

                <div className="pt-4">
                  <button 
                    onClick={() => setSubmitted(false)}
                    className="btn-outline text-xs px-6 py-2.5 rounded-xl cursor-pointer"
                  >
                    Schedule Another Meeting
                  </button>
                </div>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} id="meetingForm" className="space-y-8">
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  
                  {/* LEFT COLUMN: CLIENT DETAILS */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-2 pb-2 border-b border-slate-200 dark:border-slate-800">
                      <User size={18} className="text-blue-600 dark:text-blue-400" />
                      <h2 className="text-lg font-extrabold text-slate-900 dark:text-white tracking-tight">
                        Your Contact Information
                      </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-slate-700 dark:text-slate-200 text-sm font-semibold mb-1.5 block">
                          Full Name *
                        </label>
                        <input 
                          type="text" 
                          placeholder="John Doe"
                          required
                          value={formData.fullName}
                          onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                          className="bg-slate-50 border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/20 dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:placeholder:text-slate-500 dark:focus:bg-slate-800/90 dark:focus:border-blue-500 dark:focus:ring-4 dark:focus:ring-blue-400/25 outline-none h-11 px-4 rounded-xl text-sm font-medium w-full transition-all duration-150"
                        />
                      </div>

                      <div>
                        <label className="text-slate-700 dark:text-slate-200 text-sm font-semibold mb-1.5 block">
                          Company Name
                        </label>
                        <input 
                          type="text" 
                          placeholder="Acme Corp"
                          value={formData.company}
                          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                          className="bg-slate-50 border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/20 dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:placeholder:text-slate-500 dark:focus:bg-slate-800/90 dark:focus:border-blue-500 dark:focus:ring-4 dark:focus:ring-blue-400/25 outline-none h-11 px-4 rounded-xl text-sm font-medium w-full transition-all duration-150"
                        />
                      </div>
                    </div>

                    {/* Work Email & Phone */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-slate-700 dark:text-slate-200 text-sm font-semibold mb-1.5 block">
                          Work Email *
                        </label>
                        <input 
                          type="email" 
                          placeholder="john@company.com"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="bg-slate-50 border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/20 dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:placeholder:text-slate-500 dark:focus:bg-slate-800/90 dark:focus:border-blue-500 dark:focus:ring-4 dark:focus:ring-blue-400/25 outline-none h-11 px-4 rounded-xl text-sm font-medium w-full transition-all duration-150"
                        />
                      </div>

                      <div>
                        <label className="text-slate-700 dark:text-slate-200 text-sm font-semibold mb-1.5 block">
                          Phone Number
                        </label>
                        <input 
                          type="tel" 
                          placeholder="+91 98765 43210"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="bg-slate-50 border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/20 dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:placeholder:text-slate-500 dark:focus:bg-slate-800/90 dark:focus:border-blue-500 dark:focus:ring-4 dark:focus:ring-blue-400/25 outline-none h-11 px-4 rounded-xl text-sm font-medium w-full transition-all duration-150"
                        />
                      </div>
                    </div>

                    {/* Service Interest */}
                    <div>
                      <label className="text-slate-700 dark:text-slate-200 text-sm font-semibold mb-1.5 block">
                        Service Interest
                      </label>
                      <select 
                        value={formData.service}
                        onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                        className="bg-slate-50 border border-slate-300 text-slate-900 focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/20 dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:focus:bg-slate-800/90 dark:focus:border-blue-500 dark:focus:ring-4 dark:focus:ring-blue-400/25 outline-none h-11 px-4 rounded-xl text-sm font-medium w-full transition-all duration-150 cursor-pointer"
                      >
                        <option value="Full-Stack Website Development">Full-Stack Website Development</option>
                        <option value="Mobile App Engineering (iOS/Android)">Mobile App Engineering (iOS/Android)</option>
                        <option value="Shopify & E-Commerce Platform">Shopify & E-Commerce Platform</option>
                        <option value="Digital Growth Marketing & SEO">Digital Growth Marketing & SEO</option>
                        <option value="Custom Enterprise Software">Custom Enterprise Software</option>
                      </select>
                    </div>

                    {/* Notes */}
                    <div>
                      <label className="text-slate-700 dark:text-slate-200 text-sm font-semibold mb-1.5 block">
                        Project Overview / Notes
                      </label>
                      <textarea 
                        placeholder="Briefly describe your goals or existing platform URL..."
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        className="bg-slate-50 border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/20 dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:placeholder:text-slate-500 dark:focus:bg-slate-800/90 dark:focus:border-blue-500 dark:focus:ring-4 dark:focus:ring-blue-400/25 outline-none min-h-[100px] p-4 rounded-xl text-sm font-medium w-full transition-all duration-150"
                      />
                    </div>
                  </div>

                  {/* RIGHT COLUMN: CALENDAR & TIME SLOT SCHEDULER */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-2 pb-2 border-b border-slate-200 dark:border-slate-800">
                      <Video size={18} className="text-blue-600 dark:text-blue-400" />
                      <h2 className="text-lg font-extrabold text-slate-900 dark:text-white tracking-tight">
                        Select Date & Time
                      </h2>
                    </div>

                    <div>
                      <label className="text-slate-700 dark:text-slate-200 text-sm font-semibold mb-1.5 block">
                        Target Discovery Date *
                      </label>
                      <input 
                        type="date"
                        required
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="bg-slate-50 border border-slate-300 text-slate-900 focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/20 dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:focus:bg-slate-800/90 dark:focus:border-blue-500 dark:focus:ring-4 dark:focus:ring-blue-400/25 outline-none h-11 px-4 rounded-xl text-sm font-medium w-full transition-all duration-150 cursor-pointer"
                      />
                    </div>

                    <div>
                      <label className="text-slate-700 dark:text-slate-200 text-sm font-semibold mb-2 block">
                        Preferred Time Slot *
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                        {timeSlots.map((slot) => {
                          const isSelected = selectedTime === slot;
                          return (
                            <button
                              key={slot}
                              type="button"
                              onClick={() => setSelectedTime(slot)}
                              className={`h-10 px-3 rounded-xl text-xs font-bold transition-all cursor-pointer border ${
                                isSelected
                                  ? "bg-blue-600 dark:bg-blue-500 !text-white border-blue-600 dark:border-blue-400 shadow-md shadow-blue-500/20"
                                  : "bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-blue-400"
                              }`}
                            >
                              {slot}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                  </div>

                </div>

                <div className="hidden sm:block pt-4 border-t border-slate-200 dark:border-slate-800">
                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-12 rounded-xl font-semibold text-base flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 dark:bg-blue-500 dark:hover:bg-blue-600 dark:active:bg-blue-700 !text-white shadow-md shadow-blue-600/25 dark:shadow-[0_0_20px_rgba(59,130,246,0.35)] transition-all cursor-pointer disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={18} className="animate-spin !text-white" />
                        <span className="!text-white">Booking Meeting & Sending Invites...</span>
                      </>
                    ) : (
                      <>
                        <Calendar size={18} className="!text-white" />
                        <span className="!text-white">Confirm & Book Technical Discovery Call</span>
                        <ArrowRight size={18} className="!text-white" />
                      </>
                    )}
                  </button>
                </div>

              </form>
            )}

          </div>
        </main>

      </div>

      {/* MOBILE FIXED BOTTOM SUBMISSION BAR */}
      {!submitted && (
        <div className="fixed bottom-0 left-0 right-0 z-[120] bg-white/95 dark:bg-[#030712]/95 backdrop-blur-xl border-t border-slate-200 dark:border-slate-800 p-3 shadow-2xl block sm:hidden">
          <button
            type="submit"
            form="meetingForm"
            disabled={isSubmitting}
            className="w-full h-12 rounded-xl font-black text-sm bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 hover:from-blue-500 hover:to-indigo-600 !text-white shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2.5 transition-all cursor-pointer disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={18} className="animate-spin !text-white" />
                <span className="!text-white">Booking Call...</span>
              </>
            ) : (
              <>
                <Calendar size={18} className="!text-white" />
                <span className="!text-white font-black tracking-wide">Confirm & Book Discovery Call</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
